/**
 * Drag-aware wrapper around TicketRowItem. Used only for PINNED rows
 * inside a SortableContext. Unpinned rows render plain TicketRowItem.
 *
 * dnd-kit splits the drag node from the activator node so the row's
 * normal click target stays clickable while the drag-handle button is
 * the only thing that starts a drag.
 */

import type { ReactNode } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { TicketRowItem } from './TicketRowItem';
import type { Ticket } from '../../types';

interface SortableTicketRowProps {
  id: string;
  ticket: Ticket;
  onClick: () => void;
  extraBadges?: Parameters<typeof TicketRowItem>[0]['extraBadges'];
  onPin: () => void;
  onUnpin: () => void;
  pinning?: boolean;
  /** Optional content rendered BELOW the row (e.g. Resolved tab's
   *  "↳ Matched: …" hint line). Carried along during drag so it visually
   *  travels with the row. */
  belowRow?: ReactNode;
}

export function SortableTicketRow(props: SortableTicketRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: props.id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    // NOTE: intentionally NOT setting `willChange: 'transform'`. That hint
    // creates a new CSS stacking context preemptively, which caused the
    // action-menu popover (z-50, rendered inside the row) to be trapped
    // inside this row's context — the menu opened but rendered BEHIND the
    // next row in DOM order. Without willChange, the row only creates a
    // stacking context DURING active drag (when transform != none), at
    // which point the menu is irrelevant. Perf hit of not using the hint
    // is negligible for the rare drag interactions on this surface.
  };

  return (
    <div ref={setNodeRef} style={style} className="flex flex-col gap-0.5">
      <TicketRowItem
        ticket={props.ticket}
        extraBadges={props.extraBadges}
        onClick={props.onClick}
        onPin={props.onPin}
        onUnpin={props.onUnpin}
        pinning={props.pinning}
        dragHandleProps={{
          setNodeRef: setActivatorNodeRef,
          attributes: attributes as unknown as Record<string, unknown>,
          listeners: (listeners ?? undefined) as unknown as Record<string, unknown>,
        }}
      />
      {props.belowRow}
    </div>
  );
}
